<template>
  <div class="w-full pb-8">
    <div class="w-full bg-app-white dark:bg-app-dark-surface1 p-5">
      <Alerts
        v-if="unlinkedVerifiers.length > 0"
        variant="danger"
        :classes="{ heading: 'max-sm:text-sm max-sm:font-semibold', message: 'max-sm:text-xs' }"
        class="mt-4"
        show-icon
        heading="Action Needed: Move Verifiers to Projects"
      >
        <template #alertMessage>
          <div>
            <div class="mt-2 text-sm font-normal max-sm:text-xs">
              Move your verifiers to their respective projects by
              <b>31 October 2023</b>
              in order for verifiers to work. All verifiers without projects will be removed.
            </div>
            <div class="mt-1 text-sm font-normal max-sm:text-xs">You need a project with the same environment to move a verifier.</div>
          </div>
        </template>
      </Alerts>
      <h1 class="text-2xl font-semibold mt-5 text-app-gray-900 mb-4">Existing Verifiers</h1>
      <div class="flex flex-col sm:flex-row gap-2 justify-between">
        <div class="w-full sm:w-1/2">
          <TextField v-model="search" placeholder="Search for verifiers" trailing-icon="search-solid-icon" />
        </div>
        <div class="max-sm:w-full flex gap-2">
          <Dropdown
            v-model="networkFilter"
            :label="`Environment : ${networkDisplay}`"
            class="w-full sm:w-max member-dropdown"
            :items="networks"
            :button-props="{ variant: 'secondary', class: 'max-sm:w-full' }"
            :classes="{
              dropdownBtn: '!border-app-gray-200 !text-app-gray-900 capitalize',
            }"
          />
          <Dropdown
            v-model="statusFilter"
            :label="`Status : ${statusFilter}`"
            class="w-full sm:w-max member-dropdown"
            :items="[
              { name: 'Status: All', value: 'all' },
              { name: 'Live', value: 'Live' },
              { name: 'Processing', value: 'Processing' },
              { name: 'Rejected', value: 'Rejected' },
            ]"
            :button-props="{ variant: 'secondary', class: 'max-sm:w-full' }"
            :classes="{
              dropdownBtn: '!border-app-gray-200 !text-app-gray-900 capitalize',
              menuContainer: 'sm:!w-36',
            }"
          />
        </div>
      </div>
    </div>
    <div class="relative max-lg:overflow-x-auto overflow-y-auto table-container">
      <div v-if="loadingVerifiers" class="flex-grow flex items-center justify-center mt-36">
        <Loader />
      </div>
      <table v-else class="!w-full text-sm text-left text-app-gray-500 dark:text-app-gray-400">
        <thead class="text-xs font-semibold capitalize bg-app-gray-50 dark:bg-app-dark-surface3 border-y border-app-gray-200">
          <tr>
            <th scope="col" class="p-4 text-app-gray-500 w-[170px]">DATE CREATED</th>
            <th scope="col" class="p-4 text-app-gray-500">VERIFIER IDENTIFIER</th>
            <th scope="col" class="p-4 text-app-gray-500">TYPE</th>
            <th scope="col" class="p-4 text-app-gray-500">ENVIRONMENT</th>
            <th scope="col" class="p-4 text-app-gray-500">STATUS</th>
            <th scope="col" class="p-4 text-app-gray-500">PROJECTS</th>
            <th scope="col" class="p-4 text-app-gray-500"></th>
          </tr>
        </thead>
        <tbody>
          <VerifierItem
            v-for="(verifier, idx) in filteredVerifiers"
            :key="idx"
            :verifier="verifier"
            :team-name="teamName"
            :disabled="!hasEditPermission"
          />
        </tbody>
      </table>
      <div v-if="filteredVerifiers.length === 0">
        <div class="font-normal py-2 mt-3 text-center">No verifiers found</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Loader } from "@toruslabs/vue-components/Loader";
import { defineComponent } from "vue";

import { Alerts } from "@/components/Alerts";
import { Dropdown } from "@/components/Dropdown";
import { TextField } from "@/components/TextField";
import { teamModule, verifierModule } from "@/store";
import { CELESTE, NETWORKS, ROLE } from "@/utils/enums";

import VerifierItem from "./VerifierItem";

export default defineComponent({
  name: "Verifiers",
  components: { Alerts, Dropdown, Loader, TextField, VerifierItem },
  props: {
    teamName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    networkFilter: "all",
    statusFilter: "all",
    search: "",
  }),
  computed: {
    networks() {
      const options = NETWORKS.filter((network) => network.value !== CELESTE).map((network) => ({ name: network.name, value: network.value }));
      return [{ name: "All", value: "all" }, ...options];
    },
    networkDisplay() {
      return this.networkFilter.split("_").join(" ");
    },
    verifiers() {
      return verifierModule.legacyVerifiersGetter(this.selectedTeam.id) || [];
    },
    loadingVerifiers() {
      return !verifierModule.legacyVerifiersGetter(this.selectedTeam.id);
    },
    existingVerifiers() {
      return this.verifiers.filter((verifier) => !verifier.DeletedAt);
    },
    unlinkedVerifiers() {
      return this.existingVerifiers.filter((verifier) => !verifier.linked_projects);
    },
    filteredVerifiers() {
      return this.existingVerifiers.filter((verifier) => {
        const networkCondition = this.networkFilter === "all" ? true : verifier.network === this.networkFilter;
        const statusCondition = this.statusFilter === "all" ? true : verifier.status === this.statusFilter;
        const searchCondition = this.search === "" ? true : verifier.verifier_id.toLowerCase().includes(this.search.toLowerCase());
        return statusCondition && networkCondition && searchCondition;
      });
    },
    archivedVerifiers() {
      return this.verifiers.filter((verifier) => verifier.DeletedAt);
    },
    selectedTeam() {
      return teamModule.selectedTeamGetter(this.teamName);
    },
    hasEditPermission() {
      if (!this.teamName) return true;
      return [ROLE.owner, ROLE.admin].includes(this.selectedTeam.role);
    },
  },
});
</script>

<style lang="scss" scoped>
.table-container {
  min-height: calc(100vh - 332px);
}
</style>
